import { loadImages, autoLoadComponents } from '@/js/utils/auto-loader.js';
import { initModelSingleGalleries } from '@components/parts/model-single-gallery/model-single-gallery.js';
import { initAlleReiskosten } from '@components/parts/model-reiskosten/model-reiskosten.js';
import '@components/parts/model-tarieven/model-tarieven.js';
import '@components/parts/facility-category-filters/facility-category-filters.js';

const initModelKenmerkTables = () => {
	document.querySelectorAll( '.js-modelKenmerkTable' ).forEach( ( root ) => {
		if ( root.dataset.modelKenmerkTableInit === '1' ) {
			return;
		}

		const button = root.querySelector( '.js-modelKenmerkTableToggle' );
		const hiddenRows = Array.from( root.querySelectorAll( '.b-modelSingle__kenmerkTabelRow--hidden' ) );
		if ( ! button || hiddenRows.length === 0 ) {
			return;
		}

		root.dataset.modelKenmerkTableInit = '1';

		const showLabel = button.getAttribute( 'data-show-label' ) || 'Show more';
		const hideLabel = button.getAttribute( 'data-hide-label' ) || 'Show less';
		let expanded = false;

		const render = () => {
			hiddenRows.forEach( ( row ) => {
				row.style.display = expanded ? 'table-row' : 'none';
			} );
			button.textContent = expanded ? hideLabel : showLabel;
			button.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );
		};

		button.addEventListener( 'click', () => {
			expanded = ! expanded;
			render();
		} );

		render();
	} );
};

const initSbTarievenTables = () => {
	document.querySelectorAll( '.js-sbTarievenTable' ).forEach( ( root ) => {
		if ( root.dataset.sbTarievenTableInit === '1' ) {
			return;
		}

		const button = root.querySelector( '.js-sbTarievenTableToggle' );
		const hiddenRows = Array.from( root.querySelectorAll( '.sb-tarieven__row--hidden' ) );
		if ( ! button || hiddenRows.length === 0 ) {
			return;
		}

		root.dataset.sbTarievenTableInit = '1';

		const showLabel = button.getAttribute( 'data-show-label' ) || 'Show more';
		const hideLabel = button.getAttribute( 'data-hide-label' ) || 'Show less';
		let expanded = false;

		const render = () => {
			hiddenRows.forEach( ( row ) => {
				row.style.display = expanded ? 'table-row' : 'none';
			} );
			button.textContent = expanded ? hideLabel : showLabel;
			button.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );
		};

		button.addEventListener( 'click', () => {
			expanded = ! expanded;
			render();
		} );

		render();
	} );
};

const initFaqAccordions = () => {
	document.querySelectorAll( '.b-single__faqItem' ).forEach( ( item, index ) => {
		const button = item.querySelector( '.b-single__faqItemToggle' );
		const content = item.querySelector( '.b-single__faqItemContent' );

		if ( ! button || ! content ) {
			return;
		}

		const setOpen = ( isOpen ) => {
			button.setAttribute( 'aria-expanded', String( isOpen ) );
			content.setAttribute( 'aria-hidden', String( ! isOpen ) );
			content.style.display = isOpen ? 'block' : 'none';
			item.classList.toggle( 'is-open', isOpen );
		};

		setOpen( false );

		button.addEventListener( 'click', () => {
			const isOpen = button.getAttribute( 'aria-expanded' ) === 'true';
			setOpen( ! isOpen );
		} );
	} );
};

const initFacilityCtaModal = () => {
	const trigger = document.querySelector( '.b-faciliteitSingle__ctaButton' );
	const modal = document.getElementById( 'b-faciliteitSingle__ctaModal' );
	const closeButton = modal ? modal.querySelector( '.b-faciliteitSingle__ctaModalClose' ) : null;
	const countdown = modal ? modal.querySelector( '.js-b-faciliteitSingle__ctaCountdown' ) : null;

	if ( ! trigger || ! modal || ! closeButton || ! countdown ) {
		return;
	}

	const website = trigger.dataset.website || '';
	if ( ! website ) {
		return;
	}

	let countdownTimer = null;

	const closeModal = () => {
		modal.setAttribute( 'hidden', 'hidden' );
		modal.setAttribute( 'aria-hidden', 'true' );
		modal.classList.remove( 'is-open' );
		if ( countdownTimer ) {
			window.clearInterval( countdownTimer );
			countdownTimer = null;
		}
	};

	const openModal = () => {
		modal.removeAttribute( 'hidden' );
		modal.setAttribute( 'aria-hidden', 'false' );
		modal.classList.add( 'is-open' );
	};

	const startCountdown = () => {
		let remainingSeconds = 5;
		countdown.textContent = String( remainingSeconds );

		countdownTimer = window.setInterval( () => {
			remainingSeconds -= 1;
			countdown.textContent = String( Math.max( 0, remainingSeconds ) );

			if ( remainingSeconds <= 0 ) {
				window.clearInterval( countdownTimer );
				countdownTimer = null;
				window.open( website, '_blank', 'noopener,noreferrer' );
				closeModal();
			}
		}, 1000 );
	};

	trigger.addEventListener( 'click', () => {
		if ( countdownTimer ) {
			window.clearInterval( countdownTimer );
			countdownTimer = null;
		}
		openModal();
		startCountdown();
	} );

	closeButton.addEventListener( 'click', () => {
		closeModal();
	} );

	modal.addEventListener( 'click', ( event ) => {
		if ( event.target === modal ) {
			closeModal();
		}
	} );
};

const initFacilityMaps = () => {
	const mapElements = Array.from( document.querySelectorAll( '.b-modelSingle__map' ) ).filter( ( element ) => element.dataset.mapInit !== '1' );
	if ( mapElements.length === 0 ) {
		return;
	}

	const apiKey = mapElements[0].dataset.googleApiKey || '';
	if ( ! apiKey ) {
		return;
	}

	const renderMap = () => {
		mapElements.forEach( ( element ) => {
			if ( ! window.google || ! window.google.maps || element.dataset.mapInit === '1' ) {
				return;
			}

			element.dataset.mapInit = '1';

			const lat = Number( element.dataset.mapLat );
			const lng = Number( element.dataset.mapLng );
			const zoom = Number( element.dataset.mapZoom || 15 );

			if ( Number.isNaN( lat ) || Number.isNaN( lng ) ) {
				return;
			}

			const map = new window.google.maps.Map( element, {
				center: { lat, lng },
				zoom,
				mapTypeId: window.google.maps.MapTypeId.ROADMAP,
				disableDefaultUI: false,
				zoomControl: true,
				streetViewControl: true,
				fullscreenControl: true,
			} );

			new window.google.maps.Marker( {
				position: { lat, lng },
				map,
			} );
		} );
	};

	if ( window.google && window.google.maps ) {
		renderMap();
		return;
	}

	if ( ! document.getElementById( 'junction-google-maps-script' ) ) {
		const script = document.createElement( 'script' );
		script.id = 'junction-google-maps-script';
		script.src = `https://maps.googleapis.com/maps/api/js?key=${ encodeURIComponent( apiKey ) }`;
		script.async = true;
		script.defer = true;
		script.onload = renderMap;
		document.head.appendChild( script );
		return;
	}

	const existingScript = document.getElementById( 'junction-google-maps-script' );
	if ( existingScript.dataset.loaded === '1' ) {
		renderMap();
		return;
	}

	existingScript.addEventListener( 'load', renderMap, { once: true } );
	existingScript.dataset.loaded = '1';
};

// Initialize shared loaders
const images = loadImages();

import '@/css/main.scss';

const components = autoLoadComponents();
initModelSingleGalleries();
initAlleReiskosten();
initModelKenmerkTables();
initSbTarievenTables();
initFaqAccordions();
initFacilityCtaModal();
initFacilityMaps();

// Block labels (only active when logged in)
import '@/js/block-labels.js';